import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Modal,
  ActionBar,
  ButtonGroup,
  Dropdown,
  Icon,
  Loader,
  Input,
} from 'shared-components';
import { post } from 'shared-components/src/utils/http';
import { isExemptBin } from 'shared-components/src/utils/binExemption';
import { digitsOnly } from '../../../../utils/cardFormatting';
import {
  CONTACT_COUNTRY,
  contactCardErrors,
  etransferContactErrors,
  formatPostalCode,
} from '../../../../utils/contactCard';
import {
  CardNumberInput,
  PostalCodeInput,
  ProvinceSelect,
  CountrySelect,
  ContactFieldError,
} from '../ContactFields';
import useFieldErrors from '../ContactFields/useFieldErrors';
import {
  StyledLabel,
  ModalFormContainer,
  SelectContainer,
  InputRow,
  InputItem,
  CollapsibleSection,
  InfoRow,
} from './styles';

import SubmitButton from '../../../../components/SubmitButton';
import CancelButton from '../../../../components/CancelButton';
import Alert from '../../../../components/Alert';

const AddExternalAccountModal = ({
  toggleModal,
  setSuccess,
  onAccountAdded,
  type: propType,
}) => {
  const intl = useIntl();
  const contactExternalAccountName = useRef(null);
  // E-transfer refs
  const contactInputFirstName = useRef(null);
  const contactInputLastName = useRef(null);
  const contactInputEmail = useRef(null);
  const contactConfirmInputEmail = useRef(null);
  // Debit card refs
  const cardholderNameInput = useRef(null);
  const cardNumberInput = useRef(null);
  const cardExpiryInput = useRef(null);
  const cardCvvInput = useRef(null);
  const addressLine1Input = useRef(null);
  const addressLine2Input = useRef(null);
  const cityInput = useRef(null);
  const postalCodeInput = useRef(null);

  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(
    propType || 'external_funding_account'
  );
  const [isBinExempt, setIsBinExempt] = useState(false);
  const [cardNumberLength, setCardNumberLength] = useState(0);
  const [province, setProvince] = useState('');

  const page = 'external-accounts';

  // Type options for dropdown (only shown when type is not provided via props)
  const typeOptions = [
    {
      key: 'external_funding_account',
      val: intl.messages[`${page}-type-push`] || 'Interac e-Transfer Push',
    },
    {
      key: 'external_source_account',
      val: intl.messages[`${page}-type-pull`] || 'Interac e-Transfer Pull',
    },
    {
      key: 'personal_funding_account',
      val: intl.messages[`${page}-type-visa-direct`] || 'Debit Card - Push',
    },
    {
      key: 'personal_source_account',
      val: intl.messages[`${page}-type-interac`] || 'Debit Card - Pull',
    },
  ];

  // Check if selected type is e-transfer or debit card
  const currentType = propType || selectedType;
  const isEtransferType =
    currentType === 'external_funding_account' ||
    currentType === 'external_source_account';
  const isDebitCardType =
    currentType === 'personal_funding_account' ||
    currentType === 'personal_source_account';
  const direction = currentType === 'personal_funding_account' ? 'push' : 'pull';

  // Only the fields of the selected type are on screen, so only their refs are read.
  const readValues = () => {
    const value = ref => ref.current?.value ?? '';
    if (isEtransferType) {
      return {
        name: value(contactExternalAccountName),
        firstName: value(contactInputFirstName),
        lastName: value(contactInputLastName),
        email: value(contactInputEmail),
        confirmEmail: value(contactConfirmInputEmail),
      };
    }
    return {
      name: value(contactExternalAccountName),
      fullName: value(cardholderNameInput),
      cardNumber: value(cardNumberInput),
      expiry: value(cardExpiryInput),
      cvv: value(cardCvvInput),
      addressLineOne: value(addressLine1Input),
      city: value(cityInput),
      state: province,
      postalCode: value(postalCodeInput),
    };
  };

  const { fieldErrors, fieldChanged, validateField, validateAll, clearField, clearAll, invalid } = useFieldErrors(
    () => (isEtransferType
      ? etransferContactErrors(readValues())
      : contactCardErrors(readValues(), { strictName: true }))
  );

  const handleCardNumberChange = e => {
    const cardNumber = e.target.value.replace(/\s/g, '');
    const newIsBinExempt = isExemptBin(cardNumber);

    // Reset CVV and expiry fields when BIN exemption status changes
    if (newIsBinExempt !== isBinExempt) {
      if (cardExpiryInput.current) cardExpiryInput.current.value = '';
      if (cardCvvInput.current) cardCvvInput.current.value = '';
    }

    setCardNumberLength(cardNumber.length);
    setIsBinExempt(newIsBinExempt);
  };

  const addAccount = async () => {
    const name = contactExternalAccountName.current.value.trim();
    const accountType = propType || selectedType;

    const externalAccountPayload = {
      name,
      type: accountType,
    };

    // Build payload based on account type
    if (isEtransferType) {
      const firstName = contactInputFirstName.current.value.trim();
      const lastName = contactInputLastName.current.value.trim();
      const email = contactInputEmail.current.value.trim();
      const confirmEmail = contactConfirmInputEmail.current.value.trim();

      if (email !== confirmEmail) {
        throw new Error(
          intl.messages[`${page}-emails-mismatch-error`] ||
            'Emails do not match.'
        );
      }

      externalAccountPayload.etransfer = {
        name: `${firstName} ${lastName}`,
        email,
      };
    } else if (isDebitCardType) {
      const cardholderName = cardholderNameInput.current.value.trim();
      const cardNumber = cardNumberInput.current.value.trim();
      const expiryValue = cardExpiryInput.current.value.trim();
      const cvv = cardCvvInput.current.value.trim();
      const addressLine1 = addressLine1Input.current.value.trim();
      const addressLine2 = addressLine2Input.current.value.trim();
      const city = cityInput.current.value.trim();
      const postalCode = postalCodeInput.current.value.trim();

      const cardPayload = {
        name: cardholderName,
        number: digitsOnly(cardNumber),
        address_line1: addressLine1,
        address_line2: addressLine2,
        address_city: city,
        address_state: province,
        address_country: CONTACT_COUNTRY,
        address_postal_code: formatPostalCode(postalCode),
      };

      if (expiryValue) {
        const [expiryMonth, expiryYear] = expiryValue.split('/');
        cardPayload.expiry_month = expiryMonth;
        cardPayload.expiry_year = expiryYear;
      }

      if (cvv) {
        cardPayload.cvv = cvv;
      }

      externalAccountPayload.card = cardPayload;
    }

    try {
      const { data } = await post(
        '/api/v2/cardholder_portal/cardholder/external_accounts',
        externalAccountPayload
      );

      if (!data || !data.id) {
        throw new Error(
          intl.messages[`${page}-create-failed-error`] ||
            'Failed to create external account'
        );
      }

      return data;
    } catch (error) {
      throw new Error(
        error.message ||
          intl.messages[`${page}-create-failed-error`] ||
          'Failed to create external account'
      );
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateAll()) return;

    setLoading(true);
    setAlert(null);

    try {
      const newAccount = await addAccount();

      if (onAccountAdded) {
        onAccountAdded(newAccount);
      }

      setSuccess({
        type: 'success',
        message:
          intl.messages[`${page}-add-success-alert`] ||
          'External account added successfully',
        messageFor: 'success-create',
        page,
      });

      setLoading(false);
      toggleModal(false);
    } catch (error) {
      setAlert({ type: 'danger', message: error.message });
      setLoading(false);
      setCardNumberLength(0);
    }
  };

  return (
    <Modal>
      <ModalFormContainer>
        {alert && (
          <Alert
            alert={alert.message}
            alertFor={alert.messageFor}
            page={page}
            type={alert.type}
          />
        )}
        <h2>{intl.messages[`${page}-add-heading`] || 'Add Account'}</h2>
        {loading ? (
          <Loader />
        ) : (
          // noValidate: every field is checked as it is left and on submit, with its message
          // shown under it, instead of the browser's own bubble.
          <form noValidate onChange={fieldChanged} onSubmit={e => handleSubmit(e)}>
            {!propType && (
              <>
                <StyledLabel htmlFor="account-type-select">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-type-label`] || 'Account Type'}
                </StyledLabel>
                <SelectContainer>
                  <Dropdown
                    id="account-type-select"
                    options={typeOptions}
                    selected={selectedType}
                    updateItem={val => {
                      setSelectedType(val);
                      clearAll();
                    }}
                  />
                </SelectContainer>
              </>
            )}
            {/* Account Name - Common field */}
            <StyledLabel htmlFor="contact-name-input">
              <span>*</span>{' '}
              {intl.messages[`${page}-contact-name-input-label`] ||
                'Account Name'}
            </StyledLabel>
            <Input
              id="contact-name-input"
              type="text"
              ref={contactExternalAccountName}
              className={invalid('name')}
              onBlur={() => validateField('name')}
              required
            />
            <ContactFieldError error={fieldErrors.name} />

            {/* E-Transfer fields */}
            {isEtransferType && (
              <>
                <StyledLabel htmlFor="contact-first-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-first-name-input-label`] ||
                    'First Name'}
                </StyledLabel>
                <Input
                  id="contact-first-name-input"
                  type="text"
                  ref={contactInputFirstName}
                  className={invalid('firstName')}
                  onBlur={() => validateField('firstName')}
                  required
                />
                <ContactFieldError error={fieldErrors.firstName} />
                <StyledLabel htmlFor="contact-last-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-last-name-input-label`] ||
                    'Last Name'}
                </StyledLabel>
                <Input
                  id="contact-last-name-input"
                  type="text"
                  ref={contactInputLastName}
                  className={invalid('lastName')}
                  onBlur={() => validateField('lastName')}
                  required
                />
                <ContactFieldError error={fieldErrors.lastName} />
                <StyledLabel htmlFor="contact-email-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-email-input-label`] || 'Email'}
                </StyledLabel>
                <Input
                  id="contact-email-input"
                  type="email"
                  ref={contactInputEmail}
                  autoComplete="email"
                  maxLength="254"
                  className={invalid('email')}
                  onBlur={() => validateField('email')}
                  required
                />
                <ContactFieldError error={fieldErrors.email} />
                <StyledLabel htmlFor="contact-confirm-email-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-contact-confirm-email-input-label`] ||
                    'Confirm Email'}
                </StyledLabel>
                <Input
                  id="contact-confirm-email-input"
                  type="email"
                  ref={contactConfirmInputEmail}
                  autoComplete="email"
                  maxLength="254"
                  className={invalid('confirmEmail')}
                  onBlur={() => validateField('confirmEmail')}
                  required
                />
                <ContactFieldError error={fieldErrors.confirmEmail} />
              </>
            )}

            {/* Debit Card fields */}
            {isDebitCardType && (
              <>
                <StyledLabel htmlFor="cardholder-name-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-cardholder-name-input-label`] ||
                    'Cardholder Name'}
                </StyledLabel>
                <Input
                  id="cardholder-name-input"
                  type="text"
                  ref={cardholderNameInput}
                  className={invalid('fullName')}
                  onBlur={() => validateField('fullName')}
                  required
                />
                <ContactFieldError error={fieldErrors.fullName} />
                <StyledLabel htmlFor="card-number-input">
                  <span>*</span>{' '}
                  {intl.messages[`contact-fields-card-number-label-${direction}`]}
                </StyledLabel>
                <CardNumberInput
                  id="card-number-input"
                  direction={direction}
                  ref={cardNumberInput}
                  onChange={handleCardNumberChange}
                  className={invalid('cardNumber')}
                  onBlur={() => validateField('cardNumber')}
                  required
                />
                <ContactFieldError error={fieldErrors.cardNumber} />
                {/* CVV + Expiry - hidden by default, show only when card number >= 6 digits and BIN is not exempt */}
                <CollapsibleSection $visible={cardNumberLength >= 6 && !isBinExempt}>
                  <InputRow>
                    <InputItem>
                      <StyledLabel htmlFor="card-expiry-input">
                        <span>*</span>{' '}
                        {intl.messages[`${page}-card-expiry-input-label`] ||
                          'Expiry (MM/YYYY)'}
                      </StyledLabel>
                      <Input
                        id="card-expiry-input"
                        type="text"
                        ref={cardExpiryInput}
                        className={invalid('expiry')}
                        onBlur={() => validateField('expiry')}
                        required={cardNumberLength >= 6 && !isBinExempt}
                      />
                      <ContactFieldError error={fieldErrors.expiry} />
                    </InputItem>
                    <InputItem>
                      <StyledLabel htmlFor="card-cvv-input">
                        <span>*</span>{' '}
                        {intl.messages[`${page}-card-cvv-input-label`] ||
                          'CVV'}
                      </StyledLabel>
                      <Input
                        id="card-cvv-input"
                        type="text"
                        ref={cardCvvInput}
                        className={invalid('cvv')}
                        onBlur={() => validateField('cvv')}
                        required={cardNumberLength >= 6 && !isBinExempt}
                      />
                      <ContactFieldError error={fieldErrors.cvv} />
                    </InputItem>
                  </InputRow>
                </CollapsibleSection>
                {/* Info message when CVV/Expiry not needed */}
                <InfoRow $visible={cardNumberLength > 0 && cardNumberLength >= 6 && isBinExempt}>
                  <Icon type="info" size={16} />
                  <span>{intl.messages['external-accounts-card-noCvvNeeded'] || 'No CVV needed.'}</span>
                </InfoRow>
                <StyledLabel htmlFor="address-line1-input">
                  <span>*</span>{' '}
                  {intl.messages[`${page}-address-line1-input-label`] ||
                    'Address Line 1'}
                </StyledLabel>
                <Input
                  id="address-line1-input"
                  type="text"
                  ref={addressLine1Input}
                  className={invalid('addressLineOne')}
                  onBlur={() => validateField('addressLineOne')}
                  required
                />
                <ContactFieldError error={fieldErrors.addressLineOne} />
                <StyledLabel htmlFor="address-line2-input">
                  {intl.messages[`${page}-address-line2-input-label`] ||
                    'Address Line 2'}
                </StyledLabel>
                <Input
                  id="address-line2-input"
                  type="text"
                  ref={addressLine2Input}
                />
                <InputRow>
                  <InputItem>
                    <StyledLabel htmlFor="city-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-city-input-label`] || 'City'}
                    </StyledLabel>
                    <Input
                      id="city-input"
                      type="text"
                      ref={cityInput}
                      className={invalid('city')}
                      onBlur={() => validateField('city')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.city} />
                  </InputItem>
                  <InputItem>
                    <StyledLabel htmlFor="state-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-state-input-label`] ||
                        'Province/State'}
                    </StyledLabel>
                    <ProvinceSelect
                      id="state-input"
                      value={province}
                      onChange={value => {
                        setProvince(value);
                        clearField('state');
                      }}
                      invalid={Boolean(fieldErrors.state)}
                    />
                    <ContactFieldError error={fieldErrors.state} />
                  </InputItem>
                </InputRow>
                <InputRow>
                  <InputItem>
                    <StyledLabel htmlFor="postal-code-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-postal-code-input-label`] ||
                        'Postal Code'}
                    </StyledLabel>
                    <PostalCodeInput
                      id="postal-code-input"
                      ref={postalCodeInput}
                      className={invalid('postalCode')}
                      onBlur={() => validateField('postalCode')}
                      required
                    />
                    <ContactFieldError error={fieldErrors.postalCode} />
                  </InputItem>
                  <InputItem>
                    <StyledLabel htmlFor="country-input">
                      <span>*</span>{' '}
                      {intl.messages[`${page}-country-input-label`] || 'Country'}
                    </StyledLabel>
                    <CountrySelect id="country-input" />
                  </InputItem>
                </InputRow>
              </>
            )}
            <ActionBar>
              <ButtonGroup reverse>
                <SubmitButton />
                <CancelButton
                  onClick={e => {
                    e.preventDefault();
                    toggleModal(false);
                  }}
                />
              </ButtonGroup>
            </ActionBar>
          </form>
        )}
      </ModalFormContainer>
    </Modal>
  );
};

AddExternalAccountModal.propTypes = {
  toggleModal: PropTypes.func.isRequired,
  setSuccess: PropTypes.func.isRequired,
  onAccountAdded: PropTypes.func,
  type: PropTypes.oneOf([
    'external_funding_account',
    'external_source_account',
    'personal_funding_account',
    'personal_source_account',
  ]),
};

AddExternalAccountModal.defaultProps = {
  onAccountAdded: null,
  type: null,
};

export default AddExternalAccountModal;
